import React, { useState, useEffect, useCallback } from 'react';
import { getAgentReports, getReportsSummary } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const STATUS_LABELS = {
  new_order: 'New', order_submitted: 'Submitted', ready_to_load: 'Ready', loaded: 'Loaded',
  shipped: 'Shipped', arrived_alg: 'Arrived', cleared_port: 'Cleared', delivered: 'Delivered',
  cancelled: 'Cancelled', active: 'Active', draft: 'Draft',
};

const PIE_COLORS = ['#002DCC', '#7c3aed', '#f59e0b', '#10b981', '#ef4444', '#06b6d4', '#8b5cf6', '#ec4899'];

const fmtDZD = (v) => v != null ? `${Number(v).toLocaleString()} DA` : '-';

const ReportsPage = () => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [agents, setAgents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [agRes, sumRes] = await Promise.all([getAgentReports(params), getReportsSummary(params)]);
      setAgents(agRes.data || []);
      setSummary(sumRes.data || {});
    } catch { }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { loadData(); }, [loadData]);

  const statusChartData = summary?.orders_by_status
    ? Object.entries(summary.orders_by_status).map(([k, v]) => ({ name: STATUS_LABELS[k] || k, value: v }))
    : [];

  const monthChartData = summary?.orders_by_month
    ? [...summary.orders_by_month].reverse().map(m => ({ month: m._id, orders: m.count }))
    : [];

  return (
    <div data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground text-sm">Agent performance & business analytics</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <Label className="text-xs">From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-[140px]" />
          </div>
          <div className="flex items-center gap-1.5">
            <Label className="text-xs">To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-[140px]" />
          </div>
          {(dateFrom || dateTo) && <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); }}>Clear</Button>}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-6">
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-3">
              {[
                { label: 'Total Orders', value: summary.total_orders, color: 'text-foreground' },
                { label: 'Total Cars', value: summary.total_cars, color: 'text-blue-600' },
                { label: 'Total Revenue', value: fmtDZD(summary.total_revenue), color: 'text-emerald-600', small: true },
                { label: 'Collected', value: fmtDZD(summary.total_collected), color: 'text-green-600', small: true },
                { label: 'Outstanding', value: fmtDZD(summary.outstanding), color: 'text-red-600', small: true },
                { label: 'Stock Models', value: summary.stock_models, color: 'text-purple-600' },
                { label: 'Stock Qty', value: summary.stock_quantity, color: 'text-violet-600' },
              ].map(s => (
                <div key={s.label} className="p-4 border-2 border-border bg-white">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{s.label}</p>
                  <p className={`${s.small ? 'text-lg' : 'text-2xl'} font-bold ${s.color}`}>{s.value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Orders by Status Pie */}
            <div className="border-2 border-border bg-white p-5">
              <h3 className="font-bold mb-3">Orders by Status</h3>
              <div className="h-64">
                {statusChartData.length > 0 ? (
                  <ResponsiveContainer width="99%" height="100%">
                    <PieChart>
                      <Pie data={statusChartData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                        {statusChartData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <div className="h-full flex items-center justify-center text-muted-foreground">No data</div>}
              </div>
            </div>

            {/* Orders by Month */}
            <div className="border-2 border-border bg-white p-5">
              <h3 className="font-bold mb-3">Orders by Month</h3>
              <div className="h-64">
                {monthChartData.length > 0 ? (
                  <ResponsiveContainer width="99%" height="100%">
                    <BarChart data={monthChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="orders" fill="#002DCC" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="h-full flex items-center justify-center text-muted-foreground">No data</div>}
              </div>
            </div>
          </div>

          {/* Agent Performance Table */}
          <div className="border-2 border-border bg-white">
            <div className="p-4 border-b"><h3 className="font-bold">Agent Performance</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="agent-report-table">
                <thead>
                  <tr className="bg-slate-50 border-b text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                    <th className="px-4 py-3 text-left">Agent</th>
                    <th className="px-4 py-3 text-left">Role</th>
                    <th className="px-4 py-3 text-center">Created</th>
                    <th className="px-4 py-3 text-center">Assigned</th>
                    <th className="px-4 py-3 text-center">Total</th>
                    <th className="px-4 py-3 text-center">Cars</th>
                    <th className="px-4 py-3 text-center">Delivered</th>
                    <th className="px-4 py-3 text-right">Revenue</th>
                    <th className="px-4 py-3 text-right">Collected</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {agents.map((a) => (
                    <tr key={a.id} className="hover:bg-slate-50" data-testid={`agent-row-${a.id}`}>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold">{a.name}</p>
                        <p className="text-[10px] text-muted-foreground">{a.email}</p>
                      </td>
                      <td className="px-4 py-3"><Badge variant="secondary" className="text-[10px]">{a.role}</Badge></td>
                      <td className="px-4 py-3 text-center font-bold">{a.orders_created}</td>
                      <td className="px-4 py-3 text-center font-bold">{a.orders_assigned}</td>
                      <td className="px-4 py-3 text-center font-bold text-primary">{a.total_orders}</td>
                      <td className="px-4 py-3 text-center">{a.total_cars}</td>
                      <td className="px-4 py-3 text-center text-green-600 font-bold">{a.delivered}</td>
                      <td className="px-4 py-3 text-right text-sm">{fmtDZD(a.total_revenue)}</td>
                      <td className="px-4 py-3 text-right text-sm text-green-600">{fmtDZD(a.total_payments_collected)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
